import React, { useState, useMemo, useEffect } from 'react';
import useF22Form from '../../../hooks/useF22Form';
import { getModule, hasModule } from './registry';
import CertificadoPagadoButton from '../CertificadoPagadoButton';
import { nombreRegimen } from '../../../services/regimenesTributarios';
import '../../../styles/components/F22Form.css';

/**
 * Etapa de captura de antecedentes del formulario F-22. Solo se monta cuando
 * ModuleRenderer ya confirmó que no existe una renta calculada todavía para
 * esta empresa/año/régimen — acá el usuario completa los datos de entrada y
 * hace click en "Calcular Renta", que calcula + guarda + genera el PDF en el
 * backend en un solo paso (no hay previsualización en vivo del resultado).
 *
 * El botón del certificado de renta pagada vive también acá, y no solo en la
 * vista de la renta ya calculada: una empresa que inició actividades antes
 * pero entra al sistema recién en el año X necesita poder registrar su F-22
 * presentado ante el SII sin estar obligada a calcular años anteriores. Su
 * balance de arrastre entra por asientos contables manuales.
 */
const F22FormPage = ({ anioTributario = 2026, regimen = '14d3', pagado, onCalculado, onCertificadoCargado }) => {
  const {
    data,
    loading,
    dirty,
    calculando,
    updateField,
    updateNestedField,
    calcular,
  } = useF22Form(anioTributario, regimen, onCalculado);

  const [activeSection, setActiveSection] = useState('');

  const mod = useMemo(() => getModule(anioTributario, regimen), [anioTributario, regimen]);
  const SECTIONS = useMemo(() => (mod?.SECTIONS || []).filter((s) => s.editable), [mod]);

  useEffect(() => {
    if (SECTIONS.length > 0) {
      setActiveSection(SECTIONS[0].id);
    }
  }, [anioTributario, regimen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasModule(anioTributario, regimen) || SECTIONS.length === 0) {
    return (
      <div className="f22-loading">
        <p>Función en desarrollo</p>
      </div>
    );
  }

  const currentSection = SECTIONS.find((s) => s.id === activeSection);
  const ActiveComponent = currentSection ? mod[currentSection.component] : null;

  if (loading) {
    return (
      <div className="f22-loading">
        <div className="f22-spinner" />
        <p>Cargando datos contables...</p>
      </div>
    );
  }

  return (
    <div className="f22-page">
      <div className="f22-header">
        <div className="f22-header-left">
          <h2 className="f22-title">
            Declaración de Renta AT {anioTributario}
          </h2>
          <span className="f22-regime-badge">
            {nombreRegimen(regimen)}
          </span>
        </div>
        <div className="f22-header-right">
          {dirty && <span className="f22-unsaved-badge">Sin calcular</span>}
          <CertificadoPagadoButton
            anioTributario={anioTributario}
            regimen={regimen}
            pagado={pagado}
            onCargado={onCertificadoCargado}
          />
          <button
            className="f22-btn-save"
            onClick={() => calcular()}
            disabled={calculando}
          >
            {calculando ? 'Calculando...' : 'Calcular Renta'}
          </button>
        </div>
      </div>

      <div className="f22-nav">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            className={`f22-nav-tab ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        ))}
      </div>

      <div className="f22-content">
        {ActiveComponent && (
          <ActiveComponent
            data={data}
            computed={{}}
            updateField={updateField}
            updateNestedField={updateNestedField}
          />
        )}
      </div>
    </div>
  );
};

export default F22FormPage;
